/**
 * Admin → Sourcing Teams — typed api-client calls + TanStack Query hooks
 * (E05-F05-T04).
 *
 * Feature-owned (NOT in the shared queryKeys.ts / queries.ts). The transport uses
 * the shared `apiClient` (auto-injects the session Bearer token, normalizes the
 * response, never throws) and `unwrap` (the single throw-at-the-Query-boundary
 * helper). Contracts come from `@eer/shared` so the SPA speaks the exact same
 * shapes as the Fastify API.
 *
 * Endpoints (1:1 with the legacy n8n `manage_sourcing_teams` action router —
 * get_teams / create_team / update_team / delete_team, docs/source-n8n/reference/
 * ST.md + dashboard.js loadSourcingTeams/saveSrcTeam/deleteSrcTeam):
 *   GET    /admin/sourcing-teams       → useSourcingTeams()        (get_teams)
 *   POST   /admin/sourcing-teams       → useCreateSourcingTeam()   (create_team)
 *   PATCH  /admin/sourcing-teams/:id   → useUpdateSourcingTeam()   (update_team)
 *   DELETE /admin/sourcing-teams/:id   → useDeleteSourcingTeam()   (delete_team, SOFT)
 *
 * The legacy SPA POSTed `{ command:'manage_sourcing_teams', action, ... }` to one
 * webhook; the v2 transport is clean REST but the `command` tag is still passed
 * for the legacy-success unwrap to mirror the other admin tabs.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  sourcingTeamsListResponseSchema,
  sourcingTeamCreateResponseSchema,
  sourcingTeamOkResponseSchema,
  type SourcingTeamDTO,
  type CreateSourcingTeamInput,
  type UpdateSourcingTeamInput,
  type SourcingTeamCreateResponse,
  type SourcingTeamOkResponse,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';

/** Feature-local query key (kept out of the shared registry). */
export const SOURCING_TEAMS_QUERY_KEY = ['admin', 'sourcing-teams'] as const;

/** The legacy command tag carried on every request (manage_sourcing_teams router). */
const SOURCING_TEAMS_COMMAND = { command: 'manage_sourcing_teams' } as const;

/** Fetch all active sourcing teams (server orders by name). */
async function fetchSourcingTeams(): Promise<SourcingTeamDTO[]> {
  const result = await apiClient.get<unknown>('/admin/sourcing-teams', SOURCING_TEAMS_COMMAND);
  const body = sourcingTeamsListResponseSchema.parse(unwrap(result));
  return body.teams;
}

/** Create a sourcing team (name required; members default []). Returns the created row. */
async function postSourcingTeam(
  input: CreateSourcingTeamInput,
): Promise<SourcingTeamCreateResponse> {
  const result = await apiClient.post<unknown>(
    '/admin/sourcing-teams',
    input,
    SOURCING_TEAMS_COMMAND,
  );
  return sourcingTeamCreateResponseSchema.parse(unwrap(result));
}

/** Full-replace a sourcing team by id (name + members are replaced wholesale). */
async function patchSourcingTeam(args: {
  id: number;
  patch: UpdateSourcingTeamInput;
}): Promise<SourcingTeamOkResponse> {
  const result = await apiClient.patch<unknown>(
    `/admin/sourcing-teams/${encodeURIComponent(String(args.id))}`,
    args.patch,
    SOURCING_TEAMS_COMMAND,
  );
  return sourcingTeamOkResponseSchema.parse(unwrap(result));
}

/** SOFT-delete a sourcing team by id (is_active=false; legacy parity: always success). */
async function deleteSourcingTeamReq(id: number): Promise<SourcingTeamOkResponse> {
  const result = await apiClient.delete<unknown>(
    `/admin/sourcing-teams/${encodeURIComponent(String(id))}`,
    SOURCING_TEAMS_COMMAND,
  );
  return sourcingTeamOkResponseSchema.parse(unwrap(result));
}

/** List query — the sourcing-teams cards. */
export function useSourcingTeams(): UseQueryResult<SourcingTeamDTO[], ApiResultError> {
  return useQuery<SourcingTeamDTO[], ApiResultError>({
    queryKey: SOURCING_TEAMS_QUERY_KEY,
    queryFn: fetchSourcingTeams,
  });
}

/** Create mutation — invalidates the list so the new team appears. */
export function useCreateSourcingTeam(): UseMutationResult<
  SourcingTeamCreateResponse,
  ApiResultError,
  CreateSourcingTeamInput
> {
  const queryClient = useQueryClient();
  return useMutation<SourcingTeamCreateResponse, ApiResultError, CreateSourcingTeamInput>({
    mutationFn: postSourcingTeam,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SOURCING_TEAMS_QUERY_KEY });
    },
  });
}

/** Update mutation — invalidates the list so the edit re-renders. */
export function useUpdateSourcingTeam(): UseMutationResult<
  SourcingTeamOkResponse,
  ApiResultError,
  { id: number; patch: UpdateSourcingTeamInput }
> {
  const queryClient = useQueryClient();
  return useMutation<
    SourcingTeamOkResponse,
    ApiResultError,
    { id: number; patch: UpdateSourcingTeamInput }
  >({
    mutationFn: patchSourcingTeam,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SOURCING_TEAMS_QUERY_KEY });
    },
  });
}

/** Delete mutation (SOFT) — invalidates the list so the removed team disappears. */
export function useDeleteSourcingTeam(): UseMutationResult<
  SourcingTeamOkResponse,
  ApiResultError,
  number
> {
  const queryClient = useQueryClient();
  return useMutation<SourcingTeamOkResponse, ApiResultError, number>({
    mutationFn: deleteSourcingTeamReq,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SOURCING_TEAMS_QUERY_KEY });
    },
  });
}
